import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Clock, Globe } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { countryTimezones, getCurrentTimeInTimezone } from "@/lib/country-timezones";

/**
 * Asks for a campaign's timezone.
 *
 * Campaigns created before the timezone was mandatory have none, and cloning one used to
 * copy that emptiness forward - the clone then inherited whatever default each downstream
 * reader happened to apply. Rather than guess on the user's behalf, ask once at the moment
 * the gap actually matters.
 */
export function CampaignTimezoneDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
  title,
  description,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (timezone: string) => void;
  isPending?: boolean;
  title?: string;
  description?: string;
}) {
  const { t } = useTranslation();
  const [country, setCountry] = useState("");
  const [timezone, setTimezone] = useState("");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title || t("campaigns.timezone.title", "Choose a timezone")}</DialogTitle>
          <DialogDescription>
            {description || t("campaigns.timezone.description", "This sets when calls go out and the times the agent offers when booking a meeting.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <Label>{t("campaigns.create.selectCountry", "Select Country")}</Label>
            </div>
            <Select
              value={country}
              onValueChange={(value) => {
                setCountry(value);
                setTimezone(countryTimezones.find(c => c.code === value)?.timezones[0]?.value || "");
              }}
            >
              <SelectTrigger data-testid="select-clone-country">
                <SelectValue placeholder={t("campaigns.create.selectCountryPlaceholder", "Choose a country")} />
              </SelectTrigger>
              <SelectContent>
                {countryTimezones.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {country && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Label>{t("campaigns.create.selectTimezone", "Select Timezone")}</Label>
              </div>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger data-testid="select-clone-timezone">
                  <SelectValue placeholder={t("campaigns.create.selectTimezonePlaceholder", "Choose a timezone")} />
                </SelectTrigger>
                <SelectContent>
                  {countryTimezones.find(c => c.code === country)?.timezones.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.name} ({tz.offset})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {timezone && (
                <p className="text-xs text-muted-foreground">
                  {t("campaigns.create.currentTimeInTimezone", "Current time in this timezone")}: {getCurrentTimeInTimezone(timezone)}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={() => onConfirm(timezone)}
            disabled={!timezone || isPending}
            data-testid="button-confirm-timezone"
          >
            {t("common.confirm", "Confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
